import { useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { updatePartialSubscription } from '../stateData/partialSubscription'
import { useDispatch } from './useDispatch'
import { useFetchPrinterClaimStatus } from './useFetchPrinterClaimStatus'
import { useSignupState } from './useSignupState'
import { useAutoClaimContext } from './useAutoClaimContext'
import { useSettings } from '../stateData/settings'
import PrinterClaimStatusPoller from '../lib/printerClaimStatusPoller'
import { AxiosError } from 'axios'
import { PartialSubscription } from '../types'
import { useFlowContext, useFlowManager, useSignupSession } from './'
import { createPreenrollment } from '../actions'

export const useFindAutoClaimPrinter = (currentStep: string) => {
  const dispatch = useDispatch()
  const fetchPrinterClaimStatus = useFetchPrinterClaimStatus()
  const { partialSubscription } = useSignupState()
  const { mnsCallbacks } = useSignupSession()
  const { setAutoClaimLoading, setAutoClaimFailed, setAutoClaimError } =
    useAutoClaimContext() ?? {}
  const settingsState = useSettings()
  const { enableGenYAutoClaim } = settingsState?.data ?? {}
  const history = useHistory()
  const flowManager = useFlowManager()
  const flowContext = useFlowContext()
  const { claimFailureContinueBehavior } = flowContext.getOptions()

  const {
    genType,
    printer,
    partialPrinter,
    oobePrinterClaimStatusPollingDelay,
    oobePrinterClaimStatusPollingTimeout
  } = partialSubscription.data

  const createPreenrollmentAndRepost = useCallback(async () => {
    try {
      await dispatch(createPreenrollment())
      window.location.replace(mnsCallbacks?.repostRedirectUrl)
    } catch (error) {
      // do nothing
    }
  }, [dispatch, mnsCallbacks])

  const waitForAutoClaim = useCallback(
    async (isBackgroundExecution = false) => {
      if (genType === 'genY' && !enableGenYAutoClaim) {
        return
      }

      const startedWaitingTime = Date.now()

      try {
        const printerClaimStatusPoller = new PrinterClaimStatusPoller({
          fetchPrinterClaimStatus,
          interval: oobePrinterClaimStatusPollingDelay || 10,
          timeout: oobePrinterClaimStatusPollingTimeout || 30
        })

        const { success, claimStatus } = await printerClaimStatusPoller.poll()
        if (success) {
          return
        }

        if (claimStatus.startsWith('autoClaimRetry')) {
          const totalWaitingTime = startedWaitingTime
            ? Number(
                ((Date.now() - Number(startedWaitingTime)) / 1000).toFixed(2)
              )
            : undefined

          await dispatch(
            updatePartialSubscription({
              auto_claim_retry: claimStatus.split(':')[1],
              totalWaitingTime
            })
          )
          return
        }
      } catch (error) {
        if (genType === 'genY') {
          const { error_code, error_id } =
            (error as AxiosError)?.response?.data ?? {}

          if (error_code) {
            setAutoClaimError({ code: error_code, id: error_id })
          } else {
            setAutoClaimError({ code: 'claim_error', id: 'UNK099' })
          }

          if (!isBackgroundExecution) {
            setTimeout(() => {
              history.replace(
                flowManager.nextPath(currentStep, {
                  goToAutoClaimError: true
                })
              )
            }, 0)
          }

          throw Error('Auto Claim Failed!')
        }
      }

      if (genType === 'genY') {
        setAutoClaimError({ code: 'claim_error', id: 'UNK099' })
        if (!isBackgroundExecution) {
          setTimeout(() => {
            history.replace(
              flowManager.nextPath(currentStep, {
                goToAutoClaimError: true
              })
            )
          }, 0)
        }
      } else {
        setAutoClaimFailed(true)
      }

      throw Error('Auto Claim Failed!')
    },
    [
      dispatch,
      fetchPrinterClaimStatus,
      genType,
      oobePrinterClaimStatusPollingDelay,
      oobePrinterClaimStatusPollingTimeout,
      setAutoClaimFailed,
      setAutoClaimError,
      enableGenYAutoClaim,
      flowManager,
      history,
      currentStep
    ]
  )

  const findAutoClaimPrinter = useCallback(
    async (isBackgroundExecution = false) => {
      try {
        const { printer, partialPrinter } = (await dispatch(
          updatePartialSubscription({
            printer_id: 'autoclaim'
          })
        )) as PartialSubscription
        return printer || partialPrinter
      } catch (error) {
        const { error_code, error_id } =
          (error as AxiosError)?.response?.data ?? {}
        if (error_code) {
          setAutoClaimError({ code: error_code, id: error_id })
        } else {
          setAutoClaimError({ code: 'printer_error', id: 'GSS001' })
        }
        if (claimFailureContinueBehavior && !isBackgroundExecution) {
          createPreenrollmentAndRepost()
        }
        throw error
      }
    },
    [
      dispatch,
      setAutoClaimError,
      claimFailureContinueBehavior,
      createPreenrollmentAndRepost
    ]
  )

  const mainClaimFunction = useCallback(
    async (isBackgroundExecution = false) => {
      if (printer || partialPrinter) {
        return printer || partialPrinter
      }

      try {
        setAutoClaimLoading?.call(null, true)
        await waitForAutoClaim(isBackgroundExecution)
        return await findAutoClaimPrinter(isBackgroundExecution)
      } finally {
        setAutoClaimLoading?.call(null, false)
      }
    },
    [
      findAutoClaimPrinter,
      partialPrinter,
      printer,
      setAutoClaimLoading,
      waitForAutoClaim
    ]
  )

  return {
    findAutoClaimPrinter: mainClaimFunction,
    createPreenrollmentAndRepost
  }
}
